import { useState, useEffect, useRef, useCallback } from 'react';
import { usePost } from 'hooks';
import { aiMessage, aiMessageInteraction } from 'services/api';
import { messageTypes, keywordTypes } from '../config';

/**
 * Custom hook to handle message generation
 *
 * @param {String} messageType - Current message type
 * @param {Object} messageData - Message data
 * @param {Boolean} hasActivities - Whether the researcher has activities
 * @returns {Object} - Methods and state for message generation
 */
export function useMessageGeneration(messageType) {
  const [generatedMessages, setGeneratedMessages] = useState({});
  const [lastGeneratedMessageData, setLastGeneratedMessageData] = useState({});
  const [displayCustomKeywordsAlert, setDisplayCustomKeywordsAlert] = useState(
    false
  );
  const generatedMessageType = useRef();
  const generateButtonRef = useRef(null);

  // Interaction call for likes/dislikes
  const [{ res: messageInteractionRes }, logMessageInteraction] = usePost({
    url: aiMessageInteraction,
  });

  // Main message generation call
  const [
    { res: generatedMessageResp, error, loading: generateMessageLoading },
    generateMessage,
  ] = usePost({
    url: aiMessage,
  });

  // Update generated messages when interaction response changes
  useEffect(() => {
    if (messageInteractionRes) {
      setGeneratedMessages(prev => ({
        ...prev,
        [generatedMessageType.current]: {
          ...prev[generatedMessageType.current],
          messageFeedbackId: messageInteractionRes,
        },
      }));
    }
  }, [messageInteractionRes]);

  // Update generated messages when generation response changes
  useEffect(() => {
    if (generatedMessageResp) {
      setGeneratedMessages(x => ({
        ...x,
        [generatedMessageType.current]: generatedMessageResp,
        messageFeedbackId: null,
      }));
    }
  }, [generatedMessageResp]);

  // Handle errors in message generation
  useEffect(() => {
    if (error) {
      if (error.status === 417) {
        setDisplayCustomKeywordsAlert(true);
      } else {
        setGeneratedMessages(x => ({
          ...x,
          [generatedMessageType.current]: {
            message:
              error.status === 412
                ? error.message
                : error.status === 503
                ? 'The service is currently busy. Please try again later.'
                : 'An error occurred generating a message. Please try again later.',
            isError: true,
          },
        }));
      }
    }
  }, [error]);

  // Message submission handler
  const handleSubmit = useCallback(
    (
      message,
      selectedWorkItemLabels,
      researcherId,
      requestLocation,
      city,
      filteredTradeshows,
      hasActivities
    ) => {
      generatedMessageType.current = message.type;

      // Prepare the message data
      const messageData = {
        requestLocation,
        researcherId,
        ...message,
        messageType: message.type,
        // Only include keyword-related data if there are activities
        ...(hasActivities
          ? {
              useCustomKeywords:
                message.keywordType === keywordTypes.custom ||
                message.keywordType === keywordTypes.custonAndDefault,
              useAccountKeywords:
                message.keywordType === keywordTypes.default ||
                message.keywordType === keywordTypes.custonAndDefault,
              keywords:
                message.keywordType !== keywordTypes.default
                  ? message.keywords
                  : [],
              excludeOldWorkItems: message.excludeOldWorkItems ?? true,
              customKeywordsMustMatch: true,
              selectedWorkItemIds: Object.entries(
                selectedWorkItemLabels || {}
              ).reduce((acc, [category, items]) => {
                acc[category] = Object.values(items).map(item => item.id);
                return acc;
              }, {}),
            }
          : {
              useCustomKeywords: false,
              useAccountKeywords: false,
              keywords: [],
              excludeOldWorkItems: true,
              customKeywordsMustMatch: true,
              selectedWorkItemIds: {},
            }),
      };

      // Add filtered tradeshows for generic and meeting message types
      if (filteredTradeshows?.length && message.type === messageTypes.generic) {
        // Get up to 100 tradeshows
        messageData.filteredTradeshows = filteredTradeshows.slice(0, 100);
      }

      // Handle specific message types
      if (message.type === messageTypes.pastTradeshow) {
        messageData.messageType = messageTypes.tradeshow; // Use the same type as upcoming tradeshow
        messageData.isPastTradeshow = true; // Add a flag to indicate it's a past tradeshow
      } else if (message.type === messageTypes.meeting) {
        messageData.city = city; // Ensure the city is included for "Meet in researcher's city" type
      }

      generateMessage(messageData);
      setLastGeneratedMessageData(messageData);
      return messageData;
    },
    [generateMessage]
  );

  return {
    generatedMessages,
    generateMessageLoading,
    displayCustomKeywordsAlert,
    setDisplayCustomKeywordsAlert,
    handleSubmit,
    logMessageInteraction,
    generateButtonRef,
    generatedMessageResp,
    lastGeneratedMessageData,
  };
}
